import type { KyInstance } from 'ky';

import { convertToQueryString, ensureError, flattenFilterConditions, formatString, isDefined } from '@enterprise/core';
import type { OperationResultWithData, PagedData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { PagedTemplateDetail } from './types/paged-template-detail.type';
import type { PagedTemplateDetailsByNameQuery } from './types/paged-template-details-by-name-query.type';

/**
 * Retrieves a paged list of template details filtered by template name.
 *
 * Sends a GET request using the provided KyInstance to fetch template details,
 * supporting filtering, sorting, and pagination via the searchParams argument.
 *
 * @param instance - The KyInstance used to make the HTTP request.
 * @param searchParams - The query parameters for filtering, sorting, and pagination.
 * @returns A promise resolving to an OperationResultWithData containing paged template details.
 * @throws Will throw an error if the request fails.
 */
export const getPagedTemplateDetailsByName = async (
  instance: KyInstance,
  searchParams: PagedTemplateDetailsByNameQuery
) => {
  try {
    const { filter, sort, templateName, ...restParams } = searchParams;
    const flattenedFilters = isDefined(filter) ? flattenFilterConditions(filter) : [];

    return await instance
      .get<OperationResultWithData<PagedData<PagedTemplateDetail>>>(
        formatString(Uri.template.getPagedTemplateDetailsByName, templateName),
        {
          searchParams: {
            ...restParams,
            ...convertToQueryString(flattenedFilters, 'filter'),
            ...convertToQueryString(sort, 'sort'),
          },
        }
      )
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
};
